import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPublicGatewaySettings } from '../redux/slice/gateway.slice';
import { FaTools } from 'react-icons/fa';
import LogoHeader from '../assets/logo-header.svg';
import gsap from 'gsap';

export default function Maintenance() {
    const dispatch = useDispatch();
    const { publicSettings } = useSelector((state) => state.gateway);

    const containerRef = useRef(null);
    const contentRef = useRef(null);

    const [timeLeft, setTimeLeft] = useState(null);

    useEffect(() => {
        dispatch(fetchPublicGatewaySettings());
    }, [dispatch]);

    useEffect(() => {
        if (!publicSettings?.maintenanceEta) return;

        const updateTimer = () => {
            const difference = new Date(publicSettings.maintenanceEta) - new Date();
            if (difference <= 0) {
                setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                return;
            }

            const days = Math.floor(difference / (1000 * 60 * 60 * 24));
            const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
            const minutes = Math.floor((difference / 1000 / 60) % 60);
            const seconds = Math.floor((difference / 1000) % 60);

            setTimeLeft({ days, hours, minutes, seconds });
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [publicSettings?.maintenanceEta]);

    useEffect(() => {
        const ctx = gsap.context(() => {
            const masterTl = gsap.timeline();

            // Sweep scanline across the page on load
            masterTl.fromTo(".maintenance-scanline",
                { top: '-5%', opacity: 1 },
                { top: '105%', opacity: 0.6, duration: 0.8, ease: "power2.inOut" }
            )
                // Bring in main details smoothly
                .fromTo(contentRef.current,
                    { opacity: 0, y: 40 },
                    { opacity: 1, y: 0, duration: 1.2, ease: "power3.out" },
                    "-=0.4"
                );
        }, containerRef);
        return () => ctx.revert();
    }, []);

    const maintenanceMsg = publicSettings?.maintenanceMessage || "A new and improved experience is loading. The game will be coming soon online. Thanks for sticking with us!";
    const maintenanceHeading = publicSettings?.maintenanceHeading || "We'll Be";
    const maintenanceHeadingHighlight = publicSettings?.maintenanceHeadingHighlight || "Coming Soon";

    return (
        <div
            ref={containerRef}
            className="min-h-screen w-full bg-[#030305] flex items-center justify-center overflow-hidden relative select-none font-sans"
        >
            {/* Ambient Background Grid Pattern Overlay with moving animation */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
                <div
                    className="absolute -inset-t-1/2 w-full h-[200%] opacity-[0.07] animate-grid-move"
                    style={{
                        backgroundImage:
                            "linear-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.4) 1px, transparent 1px)",
                        backgroundSize: "40px 40px",
                    }}
                />
            </div>

            {/* Glowing neon scanlines sweeping top-to-bottom on page entry */}
            <div className="maintenance-scanline absolute left-0 w-full h-[3px] z-30 bg-gradient-to-r from-transparent via-cyber-cyan to-transparent shadow-[0_0_20px_rgba(0,240,255,0.8)] pointer-events-none" style={{ top: '-5%' }}></div>

            {/* Subtle, premium decorative blur circles */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[450px] h-[450px] bg-cyber-pink/5 rounded-full blur-[130px] pointer-events-none animate-pulse" style={{ animationDuration: '6s' }} />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] bg-cyber-blue/5 rounded-full blur-[110px] pointer-events-none animate-pulse" style={{ animationDuration: '8s' }} />

            {/* =========================================================
                FOREGROUND: CLEAN THEME-RELATED PANEL
                ========================================================= */}
            <div
                ref={contentRef}
                className="relative z-20 max-w-lg w-full mx-4 text-center space-y-8"
            >
                {/* Brand Logo Header */}
                <div className="flex justify-center mb-2">
                    <img
                        src={LogoHeader}
                        alt="CUBE GAMES"
                        className="h-10 w-auto object-contain filter drop-shadow-[0_0_15px_rgba(255,0,122,0.15)]"
                    />
                </div>

                {/* Elegant, Glassmorphic Dashboard Panel */}
                <div className="relative bg-cyber-panel/90 border border-white/5 rounded-[32px] p-8 sm:p-10 shadow-2xl backdrop-blur-3xl overflow-hidden group hover:border-cyber-pink/20 transition-all duration-500">
                    {/* Glowing neon accent line */}
                    <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-cyber-pink to-cyber-blue" />

                    <div className="space-y-6">
                        {/* Status Beacon & Floating Gear Icon */}
                        <div className="relative mx-auto w-20 h-20 flex items-center justify-center">
                            {/* Neon spinning outer threads */}
                            <div className="absolute inset-0 rounded-full border border-cyber-pink/20 animate-[spin_8s_linear_infinite]" />
                            <div className="absolute -inset-1 rounded-full border border-dashed border-cyber-blue/20 animate-[spin_12s_linear_infinite_reverse]" />

                            <div className="w-14 h-14 bg-[#0d0d1a] border border-cyber-pink/30 rounded-2xl flex items-center justify-center shadow-[0_0_20px_rgba(255,0,122,0.15)]">
                                <FaTools className="w-5 h-5 text-cyber-pink animate-bounce" style={{ animationDuration: '3.5s' }} />
                            </div>
                        </div>

                        {/* Theme Related Simple Copy */}
                        <div className="space-y-3">
                            <span className="inline-block text-[10px] font-cyber tracking-widest text-cyber-blue bg-cyber-blue/10 border border-cyber-blue/20 px-3 py-1 rounded-full uppercase font-semibold">
                                System Update
                            </span>
                            <h1 className="text-3xl sm:text-4xl font-cyber font-black tracking-widest text-white uppercase leading-none">
                                {maintenanceHeading}
                                <br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyber-pink via-cyber-purple to-cyber-blue">
                                    {maintenanceHeadingHighlight}
                                </span>
                            </h1>
                            <p className="text-gray-400 text-xs sm:text-[13px] leading-relaxed font-sans font-light max-w-sm mx-auto">
                                {maintenanceMsg}
                            </p>
                        </div>

                        {/* Countdown timer */}
                        {timeLeft && (
                            <div className="grid grid-cols-4 gap-2.5 max-w-xs mx-auto mt-6 bg-[#0a0a14] border border-white/5 p-4 rounded-2xl">
                                {[
                                    { label: 'Days', val: timeLeft.days },
                                    { label: 'Hours', val: timeLeft.hours },
                                    { label: 'Mins', val: timeLeft.minutes },
                                    { label: 'Secs', val: timeLeft.seconds }
                                ].map((slot, index) => (
                                    <div key={index} className="flex flex-col items-center">
                                        <span className="text-xl sm:text-2xl font-mono font-black text-cyber-cyan">
                                            {String(slot.val).padStart(2, '0')}
                                        </span>
                                        <span className="text-[8px] font-cyber text-gray-500 uppercase tracking-widest mt-1">
                                            {slot.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Minimalist linear pulse loader */}
                        {!timeLeft && (
                            <div className="w-24 h-[2px] bg-white/5 mx-auto relative overflow-hidden rounded-full mt-4">
                                <div className="absolute top-0 left-0 h-full w-1/2 bg-gradient-to-r from-cyber-pink to-cyber-blue animate-[marquee_2s_linear_infinite]" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Secure network disclaimer subfooter */}
                <div className="flex items-center justify-center gap-1.5 text-gray-500 text-[10px] tracking-widest font-cyber uppercase">
                    <span>Secure Connection Active</span>
                </div>
            </div>

            {/* Absolute page footer */}
            <div className="absolute bottom-8 left-0 right-0 text-center text-gray-600 text-[10px] font-mono tracking-widest uppercase z-20">
                &copy; {new Date().getFullYear()} Cube Games. All rights reserved.
            </div>

            {/* Custom keyframes for the marquee loading point */}
            <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes marquee {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(200%); }
                }
            `}} />
        </div>
    );
}
